import * as webMercatorUtils from '@arcgis/core/geometry/support/webMercatorUtils.js';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useWhichReverseGeocodeService from '@rsos/hooks/useWhichReverseGeocodeService';
import {
  TOW_PRO,
  setCoordinates,
  setPinnedLocation,
} from '@rsos/reduxDataStore/src/features/geoSelection/geoSelectionSlice';
import { needsWebMercatorConversion } from '../../../../../utils/needsWebMercatorConversion';
import type { GeocodedAddress } from '../../../../AirAmbulanceForm/AirAmbulanceForm/components/SuggestedAddress';
import useDebouncedAddressUpdate from '../../../../AirAmbulanceForm/AirAmbulanceForm/hooks/useDebouncedAddressUpdate';
import useFormReverseGeocode from '../../../../AirAmbulanceForm/AirAmbulanceForm/hooks/useFormReverseGeocode';
import { formatAddressFromObject } from '../../../../AirAmbulanceForm/AirAmbulanceForm/utils';
import usePermissionedGeocoder from '../../../AviveAED/usePermissionedGeocoder';

interface Coordinates {
  latitude: number;
  longitude: number;
}

interface GeoSelectionState {
  geoSelection: {
    [TOW_PRO]: { coordinates: [number, number] | null };
  };
}

/**
 * Where a location update came from: a click on the map (via the
 * SelectLocationBanner), the address search, or the reverse geocode that
 * fills in the address for coordinates the form already has.
 */
export type TowProLocationSource = 'map' | 'search' | 'reverse-geocode';

interface UseTowProLocationFieldOptions {
  /** Applies a resolved address + coordinates to the form. */
  onLocationResolved: (
    location: {
      address?: string;
      latitude?: number;
      longitude?: number;
    },
    source: TowProLocationSource,
  ) => void;
  /**
   * Location of the request being edited. While editing, the map always
   * shows a pin: it starts here and follows each newly selected location.
   */
  existingLocation?: Coordinates | null;
}

/**
 * Location state for the tow request form: forward geocoding with
 * suggestions for typed addresses, and reverse geocoding for coordinates
 * picked on the map through the towPro geoSelection key.
 */
export function useTowProLocationField({
  onLocationResolved,
  existingLocation = null,
}: UseTowProLocationFieldOptions) {
  const dispatch = useDispatch();
  const mapClickCoordinates = useSelector(
    (state: GeoSelectionState) => state.geoSelection[TOW_PRO].coordinates,
  );

  const [addressToGeocode, setAddressToGeocode] = useState('');
  const [coordsToReverseGeocode, setCoordsToReverseGeocode] =
    useState<Coordinates | null>(null);

  const service = useWhichReverseGeocodeService();
  const geocodedResults = usePermissionedGeocoder(
    addressToGeocode,
  ) as GeocodedAddress[];
  const { isReverseGeocoding, foundAddress } = useFormReverseGeocode({
    coordinates: coordsToReverseGeocode,
    service,
  });
  const debouncedSetAddress = useDebouncedAddressUpdate(setAddressToGeocode);

  const setMapPin = useCallback(
    (location: Coordinates | null): void => {
      dispatch(setPinnedLocation({ key: TOW_PRO, location }));
    },
    [dispatch],
  );

  const isEditing = existingLocation !== null;

  // A new request pins only a location picked on the map; a request being
  // edited starts pinned at its current location. Either way the pin must
  // not outlive the form.
  useEffect(() => {
    if (existingLocation) {
      setMapPin({
        latitude: existingLocation.latitude,
        longitude: existingLocation.longitude,
      });
    }
    return () => setMapPin(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only on open
  }, []);

  // Map click: geoSelection delivers [longitude, latitude]; consume it, then
  // clear the slice key so the same click is not reapplied.
  useEffect(() => {
    if (!mapClickCoordinates) {
      return;
    }
    const [longitude, latitude] = mapClickCoordinates;
    onLocationResolved({ latitude, longitude }, 'map');
    setMapPin({ latitude, longitude });
    setAddressToGeocode('');
    setCoordsToReverseGeocode({ latitude, longitude });
    dispatch(setCoordinates({ key: TOW_PRO, coordinates: null }));
  }, [mapClickCoordinates, dispatch, onLocationResolved, setMapPin]);

  // Reverse geocoding fills the address for coordinates picked on the map.
  useEffect(() => {
    if (!foundAddress) {
      return;
    }
    const formattedAddress = foundAddress.addressObject
      ? formatAddressFromObject(foundAddress.addressObject)
      : foundAddress.address;
    if (formattedAddress) {
      onLocationResolved({ address: formattedAddress }, 'reverse-geocode');
    }
  }, [foundAddress, onLocationResolved]);

  const handleAddressChange = useCallback(
    (value: string): void => {
      // Typing invalidates the previously resolved coordinates until a
      // suggestion is picked.
      onLocationResolved(
        {
          address: value,
          latitude: undefined,
          longitude: undefined,
        },
        'search',
      );
      // While editing, the last selected location stays pinned until a new
      // one is picked, so the pin never disappears mid-edit.
      if (!isEditing) {
        setMapPin(null);
      }
      if (value.trim().length > 3) {
        debouncedSetAddress(value);
      } else {
        setAddressToGeocode('');
      }
    },
    [debouncedSetAddress, isEditing, onLocationResolved, setMapPin],
  );

  const handleSelectSuggestion = useCallback(
    (suggestion: GeocodedAddress): void => {
      const { x, y } = suggestion.location ?? {};
      if (typeof x !== 'number' || typeof y !== 'number') {
        return;
      }

      let latitude = y;
      let longitude = x;
      if (needsWebMercatorConversion(x, y)) {
        [longitude, latitude] = webMercatorUtils.xyToLngLat(x, y);
      }

      onLocationResolved(
        {
          address: suggestion.address ?? '',
          latitude,
          longitude,
        },
        'search',
      );
      setMapPin(isEditing ? { latitude, longitude } : null);
      setAddressToGeocode('');
    },
    [isEditing, onLocationResolved, setMapPin],
  );

  return {
    geocodedResults,
    isReverseGeocoding,
    handleAddressChange,
    handleSelectSuggestion,
    /** Resolve an address for coordinates that arrived without one. */
    triggerReverseGeocode: setCoordsToReverseGeocode,
  };
}
